import { defineStore } from "pinia";
import api from "@/services/api";
// LocalStorage anahtarı — tenant ve user bazlı izolasyon
function storageKey(suffix) {
    const tenantId = process.env.VUE_APP_TENANT_ID || "main";
    // Auth store olmadan direkt localStorage'dan kullanıcı ID'si oku
    try {
        const authRaw = localStorage.getItem("transportation_auth");
        if (authRaw) {
            const auth = JSON.parse(authRaw);
            const userId = auth?.user?.id || auth?.kullanici?.id || "anon";
            return `scenarioStore_${tenantId}_${userId}_${suffix}`;
        }
    }
    catch (_) {
        // parse hatası
    }
    return `scenarioStore_${tenantId}_anon_${suffix}`;
}
function persistAktifDonem(donem) {
    try {
        localStorage.setItem(storageKey("aktifDonem"), JSON.stringify(donem));
    }
    catch (_) {
        /* localStorage erişim hatası — sessizce geç */
    }
}
function persistAktifSenaryo(senaryo) {
    try {
        localStorage.setItem(storageKey("aktifSenaryo"), JSON.stringify(senaryo));
    }
    catch (_) {
        /* localStorage erişim hatası — sessizce geç */
    }
}
function restoreAktifDonem() {
    try {
        const raw = localStorage.getItem(storageKey("aktifDonem"));
        return raw ? JSON.parse(raw) : null;
    }
    catch (_) {
        return null;
    }
}
function restoreAktifSenaryo() {
    try {
        const raw = localStorage.getItem(storageKey("aktifSenaryo"));
        return raw ? JSON.parse(raw) : null;
    }
    catch (_) {
        return null;
    }
}
/**
 * Senaryo yönetimi store (Pinia).
 * Vuex kaynak: `store/modules/scenarioStore.js` — davranış birebir.
 * Persist: aktifDonem + aktifSenaryo → localStorage (tenant + user scope)
 */
export const useScenarioStore = defineStore("scenario", {
    state: () => ({
        donemler: [],
        senaryolar: [],
        aktifDonem: restoreAktifDonem(),
        aktifSenaryo: restoreAktifSenaryo(),
        loading: false,
        error: null,
    }),
    getters: {
        aktifDonemId: (state) => state.aktifDonem?.id || null,
        aktifSenaryoId: (state) => state.aktifSenaryo?.id || null,
        secimTamamlandi: (state) => !!(state.aktifDonem && state.aktifSenaryo),
        donemlerYuklendi: (state) => state.donemler.length > 0,
    },
    actions: {
        /** Vuex: SET_LOADING */
        setLoading(val) {
            this.loading = val;
        },
        /** Vuex: SET_ERROR */
        setError(msg) {
            this.error = msg;
        },
        /** Vuex: SET_DONEMLER */
        setDonemler(list) {
            this.donemler = list;
        },
        /** Vuex: SET_SENARYOLAR */
        setSenaryolar(list) {
            this.senaryolar = list;
        },
        /** Vuex: SET_AKTIF_DONEM */
        patchAktifDonem(donem) {
            this.aktifDonem = donem;
            persistAktifDonem(donem);
        },
        /** Vuex: SET_AKTIF_SENARYO */
        patchAktifSenaryo(senaryo) {
            this.aktifSenaryo = senaryo;
            persistAktifSenaryo(senaryo);
        },
        /** Vuex: ADD_DONEM */
        addDonem(donem) {
            this.donemler.unshift(donem);
        },
        /** Vuex: UPDATE_DONEM */
        updateDonemInList(updated) {
            const idx = this.donemler.findIndex((d) => d.id === updated.id);
            if (idx > -1)
                this.donemler.splice(idx, 1, updated);
            if (this.aktifDonem?.id === updated.id) {
                this.aktifDonem = updated;
                persistAktifDonem(updated);
            }
        },
        /** Vuex: REMOVE_DONEM */
        removeDonemFromList(donemId) {
            this.donemler = this.donemler.filter((d) => d.id !== donemId);
            if (this.aktifDonem?.id === donemId) {
                this.aktifDonem = null;
                persistAktifDonem(null);
                this.aktifSenaryo = null;
                persistAktifSenaryo(null);
            }
        },
        /** Vuex: ADD_SENARYO */
        addSenaryo(senaryo) {
            this.senaryolar.unshift(senaryo);
        },
        /** Vuex: UPDATE_SENARYO */
        updateSenaryoInList(updated) {
            const idx = this.senaryolar.findIndex((s) => s.id === updated.id);
            if (idx > -1)
                this.senaryolar.splice(idx, 1, updated);
            if (this.aktifSenaryo?.id === updated.id) {
                this.aktifSenaryo = updated;
                persistAktifSenaryo(updated);
            }
        },
        /** Vuex: REMOVE_SENARYO */
        removeSenaryoFromList(senaryoId) {
            this.senaryolar = this.senaryolar.filter((s) => s.id !== senaryoId);
            if (this.aktifSenaryo?.id === senaryoId) {
                this.aktifSenaryo = null;
                persistAktifSenaryo(null);
            }
        },
        // ── Dönem ──────────────────────────────────────────────────────────────
        async fetchDonemler(durum) {
            this.setLoading(true);
            this.setError(null);
            try {
                const res = await api.getDonemler(durum);
                if (res.success)
                    this.setDonemler(res.data || []);
                else
                    this.setError(res.error || "Dönemler yüklenemedi");
            }
            catch (e) {
                this.setError(e.message || "Dönemler yüklenemedi");
            }
            finally {
                this.setLoading(false);
            }
        },
        async createDonem(payload) {
            const res = await api.createDonem(payload);
            if (res.success)
                this.addDonem(res.data);
            else
                throw new Error(res.error || "Dönem oluşturulamadı");
            return res.data;
        },
        async updateDonem({ id, ...payload }) {
            const res = await api.updateDonem(id, payload);
            if (res.success)
                this.updateDonemInList(res.data);
            else
                throw new Error(res.error || "Dönem güncellenemedi");
            return res.data;
        },
        async deleteDonem(donemId) {
            try {
                const res = await api.deleteDonem(donemId);
                if (res.success)
                    this.removeDonemFromList(donemId);
                else
                    throw new Error(res.error || "Dönem silinemedi");
            }
            catch (e) {
                throw new Error(e.response?.data?.error || e.message || "Dönem silinemedi");
            }
        },
        async donemAktifYap(donemId) {
            const res = await api.donemAktifYap(donemId);
            if (res.success)
                this.updateDonemInList(res.data);
            else
                throw new Error(res.error || "Dönem aktif yapılamadı");
            return res.data;
        },
        setAktifDonem(donem) {
            this.patchAktifDonem(donem);
            // Dönem değişince senaryoları otomatik yükle
            if (donem?.id)
                this.fetchSenaryolar(donem.id);
            else
                this.setSenaryolar([]);
        },
        // ── Senaryo ────────────────────────────────────────────────────────────
        async fetchSenaryolar(donemId) {
            this.setLoading(true);
            this.setError(null);
            try {
                const res = await api.getSenaryolar(donemId);
                if (res.success)
                    this.setSenaryolar(res.data || []);
                else
                    this.setError(res.error || "Senaryolar yüklenemedi");
            }
            catch (e) {
                this.setError(e.message || "Senaryolar yüklenemedi");
            }
            finally {
                this.setLoading(false);
            }
        },
        async createSenaryo(payload) {
            const res = await api.createSenaryo(payload);
            if (res.success)
                this.addSenaryo(res.data);
            else
                throw new Error(res.error || "Senaryo oluşturulamadı");
            return res.data;
        },
        async updateSenaryo({ id, ...payload }) {
            const res = await api.updateSenaryo(id, payload);
            if (res.success)
                this.updateSenaryoInList(res.data);
            else
                throw new Error(res.error || "Senaryo güncellenemedi");
            return res.data;
        },
        async deleteSenaryo(senaryoId) {
            try {
                const res = await api.deleteSenaryo(senaryoId);
                if (res.success)
                    this.removeSenaryoFromList(senaryoId);
                else
                    throw new Error(res.error || "Senaryo silinemedi");
            }
            catch (e) {
                const status = e.response?.status;
                const apiErr = e.response?.data?.error;
                if (status === 409 && apiErr) {
                    throw new Error(apiErr);
                }
                throw new Error(apiErr || e.message || "Senaryo silinemedi");
            }
        },
        setAktifSenaryo(senaryo) {
            this.patchAktifSenaryo(senaryo);
        },
        async kopyalaSenaryo({ senaryoId, ...payload }) {
            const res = await api.kopyalaSenaryo(senaryoId, payload);
            if (!res.success)
                throw new Error(res.error || "Senaryo kopyalanamadı");
            return res.kopyalanan;
        },
        async fetchKarsilastirma(ids) {
            const res = await api.getSenaryoKarsilastir(ids);
            if (!res.success)
                throw new Error(res.error || "Karşılaştırma yapılamadı");
            return res.senaryolar;
        },
    },
});
